import { DialogM } from "@common/components/alert/dialog/Dialog"
import TouchableRippleM from "@common/components/button/TouchableRippleM"
import TextM from "@common/components/text/TextM"
import {
    useIsActivityCameraOn,
    useIsHandRaised,
    useIsOngoingCheckpoint,
    useIsOutOfApp,
    useIsParticipantOnline,
    useIsSoundMode,
    useParticipantActiveTime,
    useTimeSinceLastCall,
} from "@common/hooks/livekit/useParticipantState"
import { TIME_SINCE_LAST_CALL_MINUTES } from "@common/liveMode/StudentsList"
import { theme } from "@common/theme/theme"
import { apiRequest } from "@data/redux/actions/api"
import { liveServiceV2 } from "@data/services/liveServiceV2"
import { hasRole } from "@data/utility/ability"
import { displayTimeSeconds } from "@data/utility/Utils"
import React, { useEffect, useRef, useState } from "react"
import { Animated, StyleSheet, View } from "react-native"
import { Tooltip } from "react-native-paper"
import FontAwesome6 from "react-native-vector-icons/FontAwesome6"
import { useDispatch, useSelector } from "react-redux"

const LiveParticipantIndicator = React.memo(({ user, liveSessionId, compact = false }) => {
    // Animation value for flashing effect
    const flashAnim = useRef(new Animated.Value(1)).current
    // State for debounced out-of-app status
    const [debouncedOutOfApp, setDebouncedOutOfApp] = useState(false)
    // Reference for the timeout
    const outOfAppTimeoutRef = useRef(null)

    // Per-user hooks — each instance only re-renders when ITS value changes
    const currentUserId = useSelector(
        (state) => state.data.currentUser?.object?.id
    )
    const dispatch = useDispatch()
    const timeSinceLastCall = useTimeSinceLastCall(user?.id)
    const isSoundOn = useIsSoundMode(user?.id)
    const isActivityCameraOn = useIsActivityCameraOn(user?.id)
    const isOutOfAppRaw = useIsOutOfApp(user?.id)
    const isHandRaised = useIsHandRaised(user?.id)
    const activeTime = useParticipantActiveTime(user?.id)
    const isOnline = useIsParticipantOnline(user?.id, currentUserId)
    const isTalkingToAgent = useIsOngoingCheckpoint(user?.id)

    // The badge shows the raised hand only when the student is online.
    const showsRaisedHand = isOnline && isHandRaised
    const isTimeSinceLastCallAlert =
        timeSinceLastCall >= TIME_SINCE_LAST_CALL_MINUTES * 60
    const rawOutOfApp = !isActivityCameraOn && isOutOfAppRaw

    // Handle debouncing for out-of-app status
    useEffect(() => {
        // Clear any existing timeout
        if (outOfAppTimeoutRef.current) {
            clearTimeout(outOfAppTimeoutRef.current)
            outOfAppTimeoutRef.current = null
        }

        if (rawOutOfApp) {
            // Set a timeout to update the debounced state after 5 seconds
            outOfAppTimeoutRef.current = setTimeout(() => {
                setDebouncedOutOfApp(true)
            }, 3000) // 3 seconds debounce
        } else {
            // Reset immediately when they're back in the app
            setDebouncedOutOfApp(false)
        }

        // Cleanup on component unmount
        return () => {
            if (outOfAppTimeoutRef.current) {
                clearTimeout(outOfAppTimeoutRef.current)
            }
        }
    }, [rawOutOfApp])

    // Use the debounced version for display logic
    const isOutOfApp = debouncedOutOfApp

    // Determine which indicator to show based on priority
    let icon,
        label,
        tooltipText,
        color,
        dialogText1,
        dialogText2,
        champion = "abou"
    let isErrorCase = false

    if (!isOnline) {
        champion = "abou"
        icon = "stopwatch"
        label = timeSinceLastCall > 0 ? displayTimeSeconds(timeSinceLastCall) : "-"
        tooltipText = "Dernier échange"
        dialogText1 = `⏱️ Temps depuis le dernier échange avec ${user?.display_name}`
        dialogText2 = `L'élève est en mode concentré depuis la durée affichée.`
        color = theme.colors.purple + "33"
    } else if (isHandRaised) {
        // Priority 1: Hand raised
        champion = "abou"
        icon = "hand"
        tooltipText = "Main levée"
        dialogText1 = `✋ ${user?.display_name} a levé la main`
        dialogText2 = `L'élève a besoin d'aide ou a une question.`
        color = theme.colors.accent + "EE"
    } else if (isOutOfApp) {
        // Priority 2: Out of app (debounced)
        champion = "abou"
        icon = "ghost"
        tooltipText = "Hors appli"
        dialogText1 = `👻 ${user?.display_name} est sorti de son écran d'activité`
        dialogText2 = `Il est sur une autre appli ou a verrouillé son appareil, c'est peut-être le moment de le relancer !`
        color = theme.colors.error + "EE"
        isErrorCase = true
    } else if (isTalkingToAgent) {
        champion = "sam"
        icon = "robot"
        tooltipText = "En conversation avec Sam"
        dialogText1 = `🤖 ${user?.display_name} est en conversation avec Sam`
        dialogText2 = `L'élève est en train d'interagir avec l'assistant Sam.`
        color = theme.colors.info + "88"
    } else if (isTimeSinceLastCallAlert) {
        // Priority 3: Time since last call alert
        champion = "abou"
        icon = "stopwatch"
        label = timeSinceLastCall > 0 ? displayTimeSeconds(timeSinceLastCall) : "-"
        tooltipText = "Dernier échange"
        dialogText1 = `⏱️ ${user?.display_name} n'a pas échangé avec toi depuis plus de 10 minutes`
        dialogText2 = `Il est peut-être temps de vérifier son avancement.`
        color = theme.colors.error + "EE"
        isErrorCase = true
    } else if (isActivityCameraOn) {
        // Priority 4: Activity camera on
        champion = "abou"
        icon = "camera"
        tooltipText = "En prise de photo"
        dialogText1 = `📷 ${user?.display_name} est en train de prendre une photo sur son activité`
        dialogText2 = ``
        color = theme.colors.primary + "33"
    } else {
        // Default: Chronometer
        if (isSoundOn) {
            // If sound on, show active time
            champion = "abou"
            icon = "volume-high"
            label = activeTime > 0 ? displayTimeSeconds(activeTime) : "-"
            tooltipText = "Temps d'échange"
            dialogText1 = `⏰ Temps d'échange avec ${user?.display_name}`
            dialogText2 = `Le son a été activé pour cet élève pendant la durée affichée.`
            color = theme.colors.success + "33"
        } else {
            // Otherwise, show time since last call
            champion = "abou"
            icon = "stopwatch"
            label = timeSinceLastCall > 0 ? displayTimeSeconds(timeSinceLastCall) : "-"
            tooltipText = "Dernier échange"
            dialogText1 = `⏱️ Temps depuis le dernier échange avec ${user?.display_name}`
            dialogText2 = `L'élève est en mode concentré depuis la durée affichée.`
            color = theme.colors.purple + "33"
        }
    }

    // Setup flashing animation for error cases
    useEffect(() => {
        let flashingAnimation

        if (isErrorCase) {
            flashingAnimation = Animated.loop(
                Animated.sequence([
                    Animated.timing(flashAnim, {
                        toValue: 0.4,
                        duration: 700,
                        useNativeDriver: true,
                    }),
                    Animated.timing(flashAnim, {
                        toValue: 1,
                        duration: 700,
                        useNativeDriver: true,
                    }),
                ])
            )

            flashingAnimation.start()
        } else {
            // Reset to full opacity for non-error cases
            flashAnim.setValue(1)
        }

        return () => {
            if (flashingAnimation) {
                flashingAnimation.stop()
            }
        }
    }, [isErrorCase, flashAnim])

    const showDialog = () => {
        DialogM.show({
            champion: champion,
            text1: dialogText1,
            text2: dialogText2,
            exitButton: true,
            onPressBack: () => {},
            // OK acknowledges a raised hand: it is the only gesture left to the
            // tutor of an individual course, whose sound toggle switches nothing.
            ...(showsRaisedHand && {
                onPress: () =>
                    dispatch(
                        apiRequest(
                            liveServiceV2.lowerHandsV2,
                            { id: liveSessionId },
                            { user_ids: [user?.id] }
                        )
                    ),
            }),
        })
    }

    // Skip rendering for id 0 or if we don't have a user
    if (!user || user?.id === 0 || hasRole(user, "tutor")) {
        return null
    }

    return (
        <View style={compact ? styles.outerContainerCompact : styles.outerContainer}>
            <Tooltip
                title={tooltipText}
                enterTouchDelay={500}
                leaveTouchDelay={500}
                placement="bottom"
            >
                <TouchableRippleM
                    style={styles.innerTouchable}
                    onPress={showDialog}
                >
                    <Animated.View
                        style={[
                            compact ? styles.iconContainerCompact : styles.iconContainer,
                            {
                                backgroundColor: color,
                                opacity: isErrorCase ? flashAnim : 1,
                            },
                        ]}
                    >
                        <FontAwesome6
                            solid
                            name={icon}
                            size={compact ? 10 : 16}
                            color={theme.colors.white}
                        />
                        {label && !compact && (
                            <TextM
                                fontSize={10}
                                color={theme.colors.white}
                                style={styles.label}
                            >
                                {label}
                            </TextM>
                        )}
                    </Animated.View>
                </TouchableRippleM>
            </Tooltip>
        </View>
    )
})

const styles = StyleSheet.create({
    outerContainer: {
        position: "absolute",
        top: 5,
        left: 5,
        zIndex: 10,
    },
    outerContainerCompact: {
        position: "absolute",
        top: 2,
        left: 2,
        zIndex: 10,
    },
    innerTouchable: {
        borderRadius: 20,
    },
    iconContainer: {
        padding: 3,
        borderRadius: 20,
        alignItems: "center",
        justifyContent: "center",
        width: 40,
        height: 40,
    },
    iconContainerCompact: {
        padding: 2,
        borderRadius: 12,
        alignItems: "center",
        justifyContent: "center",
        width: 24,
        height: 24,
    },
    label: {
        marginTop: 2,
        textAlign: "center",
    },
})

export default LiveParticipantIndicator
